import { $fetch } from 'ofetch';
import type { IHorarios } from '~~/interfaces';
import { PromiseReplicate } from '~~/helpers';

export class HorarioService {
  public db: PouchDB.Database<IHorarios> | undefined;
  public remoteDB: PouchDB.Database<IHorarios> | undefined;
  public baseUrl: string;
  public version: string;
  public $fetch;
  private minTime: number;

  constructor(
    db: PouchDB.Database<IHorarios> | undefined,
    remoteDB: PouchDB.Database<IHorarios> | undefined,
    baseUrl: string,
    version: string,
    minTime: number = 300
  ) {
    this.db = db;
    this.remoteDB = remoteDB;
    this.baseUrl = baseUrl;
    this.version = version;
    this.$fetch = $fetch;
    this.minTime = minTime;
  }

  public async getFromPouch(
    idServicio: string,
    idSalida: string,
    idLlegada: string
  ): Promise<IHorarios['H'] | []> {
    const doc = await this.db?.get<IHorarios>(
      `${idServicio}>${idSalida}>${idLlegada}`
    );

    return doc ? doc?.H : [];
  }

  public async getFromAPI(
    idServicio: string,
    idSalida: string,
    idLlegada: string
  ): Promise<IHorarios['H']> {
    try {
      const response = await this.$fetch<IHorarios>(
        `${this.baseUrl}/horarios${this.version}/${idServicio}>${idSalida}>${idLlegada}`
      );
      return response.H;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async fetchHorario(
    idServicio: string,
    idSalida: string,
    idLlegada: string,
    external = false
  ): Promise<IHorarios['H'] | []> {
    const timeStart = performance.now();
    let data: IHorarios['H'] | [] = [];

    try {
      if (external) {
        data = await this.getFromAPI(idServicio, idSalida, idLlegada);
      } else {
        data = await this.getFromPouch(idServicio, idSalida, idLlegada).catch(
          async (error) => {
            // TODO: Type this correctly
            if (error.status === 404 || error.message === 'missing') {
              return this.getFromAPI(idServicio, idSalida, idLlegada);
            }
            throw error;
          }
        );
      }
    } catch (error) {
      console.error('Error fetching horario:', error);
    } finally {
      const timeEnd = performance.now();
      const deltaT = timeEnd - timeStart;

      if (this.minTime && import.meta.client && deltaT < this.minTime) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.minTime - deltaT)
        );
      }
    }
    return data;
  }

  async syncDatabases(updateFn: (percent: number) => void) {
    try {
      await this.compact();

      if (!this.db || !this.remoteDB)
        throw new Error('Databases not initialized');

      await PromiseReplicate(this.db, this.remoteDB, updateFn).then(
        (d) => d.docs_written
      );

      return this.getAllHorarios();
    } catch (error) {
      // TODO: Type this correctly
      console.error('Database sync error:', error);
      return Promise.reject(error);
    }
  }

  async getAllHorarios() {
    try {
      await this.compact();
      return this.db?.allDocs({ include_docs: true });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async compact() {
    return this.db?.compact().catch((e) => console.log(e, 'Compact err'));
  }
}
